/**
 * Mapa simplificado de posições (eixo × lado) para a inspeção posicional — o mesmo
 * desenho serve a freios, cubos, rolamentos, suspensão e ao mapa de pneus: cada
 * posição mostra a condição, a primeira medida e o pneu (quando houver).
 */
import type { MaintenanceInspectionConditionDto, MaintenanceInspectionPositionDto } from "@bi/types";
import { COND_STYLE } from "./work-order-inspection-ui";

export interface PositionCell {
  pos: MaintenanceInspectionPositionDto;
  condition: MaintenanceInspectionConditionDto;
  /** valor principal já formatado (ex.: "5 mm") */
  value: string | null;
  /** posição já tem intervenção registrada */
  intervened: boolean;
}

const lado = (key: string): "L" | "R" | null => (/-LE(-|$)/.test(key) ? "L" : /-LD(-|$)/.test(key) ? "R" : null);
const curto = (key: string) => key.replace(/^E\d+-/, "");

export function PositionMap({
  cells, selected, onSelect,
}: { cells: PositionCell[]; selected: string | null; onSelect: (key: string) => void }) {
  const grupos = new Map<string, PositionCell[]>();
  for (const c of cells) grupos.set(c.pos.group ?? "", [...(grupos.get(c.pos.group ?? "") ?? []), c]);
  const temLados = cells.some((c) => lado(c.pos.key));

  const Celula = ({ c }: { c: PositionCell }) => {
    const st = COND_STYLE[c.condition];
    const ativa = selected === c.pos.key;
    return (
      <button
        type="button"
        onClick={() => onSelect(c.pos.key)}
        aria-pressed={ativa}
        aria-label={`${c.pos.label}: ${c.condition === "not_inspected" ? "não inspecionado" : c.condition}`}
        className={`relative min-h-[3.5rem] min-w-[4rem] rounded-lg border-2 bg-white px-1.5 py-1 text-center text-xs transition ${st.ring} ${ativa ? "ring-2 ring-tm-500 ring-offset-1" : "hover:bg-surface-muted"}`}
      >
        <span className="block font-mono text-[0.6875rem] font-semibold text-gray-800">{curto(c.pos.key)}</span>
        <span className="block text-[0.6875rem] text-gray-600">{c.value ?? (c.condition === "not_inspected" ? "—" : COND_STYLE[c.condition].symbol)}</span>
        {c.pos.tire?.code && <span className="block truncate text-[0.625rem] text-gray-400">#{c.pos.tire.code}</span>}
        {c.condition !== "not_inspected" && (
          <span className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[0.625rem] text-white ${st.dot}`} aria-hidden>
            {c.intervened ? "🔧" : st.symbol}
          </span>
        )}
      </button>
    );
  };

  if (!temLados) {
    return (
      <div className="flex flex-wrap gap-2" role="group" aria-label="Posições">
        {cells.map((c) => <Celula key={c.pos.key} c={c} />)}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1.5" role="group" aria-label="Mapa de posições">
      <span className="text-[0.625rem] font-medium uppercase tracking-wide text-gray-400">↑ Frente</span>
      {[...grupos.entries()].map(([g, cs]) => (
        <div key={g} className="flex items-center gap-2">
          <div className="flex gap-1.5">{cs.filter((c) => lado(c.pos.key) === "L").map((c) => <Celula key={c.pos.key} c={c} />)}</div>
          <div className="flex h-10 w-16 flex-col items-center justify-center rounded bg-gray-100 text-[0.625rem] text-gray-500">
            <span className="h-0.5 w-full bg-gray-300" />
            <span className="my-0.5">{g || "Eixo"}</span>
            <span className="h-0.5 w-full bg-gray-300" />
          </div>
          <div className="flex gap-1.5">{cs.filter((c) => lado(c.pos.key) === "R").map((c) => <Celula key={c.pos.key} c={c} />)}</div>
        </div>
      ))}
    </div>
  );
}
